import type { UnknownAction } from '@reduxjs/toolkit';
import {
  type CareOrder,
  type ExecutionReport,
  FieldValidationLevel,
  ParentOrderTypeEnum,
  type Quote,
  RFQTradingProtocolEnum,
  toBig,
  toBigWithDefault,
} from '@talos/kyoko';
import { Big } from 'big.js';
import { streamingDataSlice } from '../../../providers/AppStateProvider/streamingDataSlice';
import type { AppState, AppStateListenerStart, AppThunkDispatch } from '../../../providers/AppStateProvider/types';
import type { PrimeOMSParams } from '../NewOrder/types';
import type { OMSView } from '../OMSView';
import type { PrimeRFQParams } from './types';

export type OrderParentType = ParentOrderTypeEnum | 'unknown';
type CareOrderQuantityValidationInput = Pick<CareOrder, 'AvailableQty'>;

export function isCareOrWorkingOrderParentType(
  parentType: OrderParentType | undefined
): parentType is typeof ParentOrderTypeEnum.CareOrder | typeof ParentOrderTypeEnum.WorkingOrder {
  return parentType === ParentOrderTypeEnum.CareOrder || parentType === ParentOrderTypeEnum.WorkingOrder;
}

export function careOrderToPrimeRFQParams(careOrder: CareOrder): PrimeRFQParams {
  return {
    group: careOrder.Group,
    symbol: careOrder.Symbol,
    side: undefined,
    currency: careOrder.Currency,
    orderQty: careOrder.AvailableQty,
    parent: {
      id: careOrder.OrderID,
      type: careOrder.isNetted ? ParentOrderTypeEnum.WorkingOrder : ParentOrderTypeEnum.CareOrder,
    },
    externalID: careOrder.ExternalGroup,
    externalComments: careOrder.ExternalComments,
    settleDate: careOrder.SettleDate,
    subAccount: careOrder.SubAccount,
    defaultToEmptySubAccount: true, // If the subAccount value above is undefined, the rfq form should default to empty

    rfqTradingProtocol: RFQTradingProtocolEnum.Negotiated, // Care order-primed RFQs are always defaulted to the "Negotiated" protocol
  };
}

export function careOrderToPrimeOrderParams(careOrder: CareOrder): PrimeOMSParams {
  return {
    group: careOrder.Group,
    symbol: careOrder.Symbol,
    side: careOrder.Side,
    currency: careOrder.Currency,
    orderQty: careOrder.AvailableQty,
    parent: {
      id: careOrder.OrderID,
      type: careOrder.isNetted ? ParentOrderTypeEnum.WorkingOrder : ParentOrderTypeEnum.CareOrder,
    },
    subAccount: careOrder.SubAccount,
    ignoreCounterCurrencyDefault: true,
  };
}

/**
 * Try to grab the ParentOrderType from the entities we work with in the RFQSlice. Either the entity has a ParentOrderType straight up,
 * and if so, just use that. But not all records will be backfilled. Secondly, some Quotes in the system will have a CareOrder enriched onto it,
 * in which case we accept that shortcut and use that.
 *
 * Else, if this fn fails to grab anything, the caller will proceed to use the async resolution function `resolveParentTypeFromParentOrder`
 */
export function getParentTypeFromParentOrder(
  childOrder: Pick<ExecutionReport, 'ParentOrderType'> | Pick<Quote, 'ParentOrderType' | 'careOrder'>
): OrderParentType | undefined {
  return (
    childOrder.ParentOrderType ?? ('careOrder' in childOrder ? childOrder.careOrder?.myParentOrderType : undefined)
  );
}

/**
 * Resolves whether a parent ID belongs to a CareOrder or WorkingOrder.
 *
 * Backwards compatibility fallback for child RFQs/orders that only expose a parent ID. Backend will
 * backfill ParentOrderType for CareOrder and WorkingOrder children, so new entities should not need
 * these websocket lookups. Once supported entities reliably carry ParentOrderType, remove this probe
 * and rely on the entity field directly.
 */
export async function resolveParentTypeFromParentOrder(
  parentID: string,
  dispatch: AppThunkDispatch
): Promise<OrderParentType | undefined> {
  const queryArg = { OrderID: parentID };
  const careOrderRequest = dispatch(streamingDataSlice.endpoints.getCareOrders.initiate(queryArg));
  const workingOrderRequest = dispatch(streamingDataSlice.endpoints.getWorkingOrders.initiate(queryArg));

  try {
    // Treat empty/error responses as rejections so Promise.any returns the first stream that finds the parent,
    // falling through to the other if the first didn't match. If neither matches, AggregateError -> "unknown".
    return await Promise.any([
      careOrderRequest.then(result => (result.data?.has(parentID) ? ParentOrderTypeEnum.CareOrder : Promise.reject())),
      workingOrderRequest.then(result =>
        result.data?.has(parentID) ? ParentOrderTypeEnum.WorkingOrder : Promise.reject()
      ),
    ]);
  } catch {
    return 'unknown' as const;
  } finally {
    careOrderRequest.unsubscribe();
    workingOrderRequest.unsubscribe();
  }
}

export function getParentOrderEndpoint(parentType: OrderParentType | undefined) {
  return parentType === ParentOrderTypeEnum.WorkingOrder
    ? streamingDataSlice.endpoints.getWorkingOrders
    : streamingDataSlice.endpoints.getCareOrders;
}

export function startCareOrderParentListener({
  startListening,
  matcher,
  openedView,
  selectParentID,
  selectParentType,
  setCurrentCareOrder,
}: {
  startListening: AppStateListenerStart;
  matcher: (action: UnknownAction) => boolean;
  openedView: OMSView;
  selectParentID: (state: AppState) => string | undefined;
  selectParentType: (state: AppState) => OrderParentType | undefined;
  setCurrentCareOrder: (careOrder: CareOrder) => UnknownAction;
}) {
  return startListening({
    predicate: action => matcher(action),
    effect: async (_, listener) => {
      listener.cancelActiveListeners();

      const orderID = selectParentID(listener.getState());
      if (orderID == null) {
        return;
      }

      const parentType = selectParentType(listener.getState());
      if (!isCareOrWorkingOrderParentType(parentType)) {
        return;
      }

      const queryArg = { OrderID: orderID };
      const parentOrderEndpoint = getParentOrderEndpoint(parentType);
      const selectCareOrder = parentOrderEndpoint.select(queryArg); // depending on the parent type, will either look in the care order or working order cache
      const selectCurrentCareOrder = (state: AppState) => selectCareOrder(state).data?.get(orderID);

      const initialCareOrder = selectCurrentCareOrder(listener.getState());
      if (initialCareOrder) {
        // If the care order we requested already exists in the cache, return that immediately
        listener.dispatch(setCurrentCareOrder(initialCareOrder));
      }

      const unsubscribeCareOrderListener = startListening({
        predicate: (_, next, prev) => selectCurrentCareOrder(next) !== selectCurrentCareOrder(prev),
        effect: (_, listener) => {
          const careOrder = selectCurrentCareOrder(listener.getState());
          if (careOrder) {
            listener.dispatch(setCurrentCareOrder(careOrder));
          }
        },
      });

      // Kick off the ws subscription
      const careOrders = listener.dispatch(parentOrderEndpoint.initiate(queryArg));

      listener.signal.addEventListener('abort', () => {
        careOrders.unsubscribe();
        unsubscribeCareOrderListener();
      });

      await listener.condition(() => {
        const next = listener.getState();
        const didCloseForm = next.OMS.openedView !== openedView;
        const didChangeParentID = selectParentID(next) !== orderID;
        return didCloseForm || didChangeParentID;
      });
    },
  });
}

export function getCareOrderQuantityValidationResult({
  orderQty,
  currentCareOrder,
  orderBeingModified,
}: {
  orderQty: string | undefined;
  currentCareOrder: CareOrderQuantityValidationInput | undefined;
  orderBeingModified?: Pick<ExecutionReport, 'OrderQty'>;
}) {
  if (!orderQty) {
    return null;
  }

  if (!currentCareOrder) {
    return {
      message: 'Loading care order...',
      level: FieldValidationLevel.Error,
    };
  }

  let availableQty = toBig(currentCareOrder.AvailableQty) ?? Big(0);

  if (orderBeingModified) {
    // When modifying an order with a parent care order, the total available qty is our child order's OrderQty + CareOrder.AvailableQty.
    // This is because our Order.OrderQty is already accounted for in the CareOrder.AvailableQty if that makes sense.
    availableQty = availableQty.plus(toBigWithDefault(orderBeingModified.OrderQty, 0));
  }

  if (toBigWithDefault(orderQty, 0).gt(availableQty)) {
    return {
      message: `Quantity cannot exceed available care order quantity: ${availableQty.toFixed()}`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
}
